import React, { Fragment, useEffect, useState } from "react";

import {
    ColumnDef,
    VisibilityState,
    ExpandedState,
    Row,
    Table as TableInstance,
    Updater,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import "./Table.scss";

export type TableProps<T, P = void> = {
    data: T[];
    columns: ColumnDef<T>[];
    subComponentProps?: P;
    subRowHandler?: (id: number) => void;
    renderSubComponent?: (props: {
        row: Row<T>;
        subRowHandler?: (id: number) => void;
        subComponentProps?: P;
    }) => JSX.Element;
    getRowCanExpand?: (row: Row<T>) => boolean;
    className?: string;
    borderRadius?: "small" | "normal";
    hideAllSubRows?: boolean;
    enableColumnVisibility?: boolean;
    columnVisibility?: VisibilityState;
    onColumnVisibilityChange?: (updater: Updater<VisibilityState>) => void;
    renderTopToolbar?: (table: TableInstance<T>) => React.ReactNode;
};

function Table<T, P = void>({
    data,
    columns,
    subComponentProps,
    subRowHandler,
    renderSubComponent,
    getRowCanExpand,
    className,
    borderRadius,
    enableColumnVisibility = false,
    columnVisibility: controlledColumnVisibility,
    onColumnVisibilityChange,
    renderTopToolbar,
}: TableProps<T, P>): JSX.Element {
    const [expanded, setExpanded] = React.useState<ExpandedState>({});
    const [internalColumnVisibility, setInternalColumnVisibility] =
        useState<VisibilityState>({});

    const isOpenObjectExists = data.find((obj) => (obj as any).isOpen === true);

    useEffect(() => {
        setExpanded(isOpenObjectExists ? { 0: true } : {});
    }, [data, isOpenObjectExists]);

    const isControlled =
        controlledColumnVisibility !== undefined &&
        onColumnVisibilityChange !== undefined;
    const columnVisibility = isControlled
        ? controlledColumnVisibility
        : internalColumnVisibility;
    const handleColumnVisibilityChange = isControlled
        ? onColumnVisibilityChange
        : setInternalColumnVisibility;

    const table = useReactTable<T>({
        data,
        columns,
        state: {
            expanded: expanded,
            ...(enableColumnVisibility && { columnVisibility }),
        },
        onExpandedChange: setExpanded,
        ...(enableColumnVisibility && {
            onColumnVisibilityChange: handleColumnVisibilityChange,
        }),
        getRowCanExpand,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    return (
        <div className={`table_container ${className}`}>
            <table
                border-radius={borderRadius}
                className={`table ${className}`}
            >
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                const size = header.getSize();
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        data-column-id={header.column.id}
                                        style={{
                                            width: size !== 150 ? `${size}px` : undefined,
                                            minWidth: header.column.columnDef.minSize ? `${header.column.columnDef.minSize}px` : undefined,
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef
                                                      .header,
                                                  header.getContext(),
                                              )}
                                    </th>
                                );
                            })}
                            {renderTopToolbar && (
                                <th className="table_toolbar_cell">
                                    {renderTopToolbar(table)}
                                </th>
                            )}
                        </tr>
                    ))}
                </thead>
                {data.length ? (
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Fragment key={row.id}>
                                    <tr
                                        onClick={(e) => {
                                            if (
                                                (e.target as HTMLElement)
                                                    .tagName !== "INPUT" &&
                                                (e.target as HTMLElement)
                                                    .tagName !== "BUTTON"
                                            ) {
                                                row.getToggleExpandedHandler()();
                                            }
                                        }}
                                    >
                                        {/* first row is a normal row */}
                                        {row.getVisibleCells().map((cell) => {
                                            const size = cell.column.getSize();
                                            return (
                                                <td
                                                    key={cell.id}
                                                    data-column-id={cell.column.id}
                                                    style={{
                                                        width: size !== 150 ? `${size}px` : undefined,
                                                        minWidth: cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined,
                                                    }}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef
                                                            .cell,
                                                        cell.getContext(),
                                                    )}
                                                </td>
                                            );
                                        })}
                                        {renderTopToolbar && (
                                            <td className="table_toolbar_cell"></td>
                                        )}
                                    </tr>
                                    {row.getIsExpanded() &&
                                        row.getCanExpand() && (
                                            <tr className="sub_row">
                                                <td
                                                    colSpan={
                                                        row.getVisibleCells()
                                                            .length + (renderTopToolbar ? 1 : 0)
                                                    }
                                                >
                                                    {renderSubComponent !==
                                                        undefined &&
                                                        renderSubComponent({
                                                            row,
                                                            subRowHandler,
                                                            subComponentProps,
                                                        })}
                                                </td>
                                            </tr>
                                        )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                ) : null}
            </table>
        </div>
    );
}

export default Table;
